import { type Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { MORFI_OPINION_PROGRAM_ID, TOKEN_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import {
  createBuildResult,
  createIdempotentAtaIx,
  invokeMethod,
  uniqueSignerKeys,
} from './common.js';

const VAULT_SEED: Buffer = Buffer.from('vault');
const PRIZE_VAULT_SEED: Buffer = Buffer.from('prize');
const EVENT_AUTHORITY_SEED: Buffer = Buffer.from('__event_authority');

interface MarketAccountV2 {
  config: PublicKey;
}

interface MarketConfigAccountV2 {
  quoteMint: PublicKey;
}

interface AnchorProgramAccounts {
  account: {
    market: { fetch(address: PublicKey): Promise<MarketAccountV2> };
    marketConfig: { fetch(address: PublicKey): Promise<MarketConfigAccountV2> };
  };
}

function derivePda(seeds: Buffer[], programId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(seeds, programId)[0];
}

function derivePrizePoolVaultPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([VAULT_SEED, PRIZE_VAULT_SEED, market.toBuffer()], programId);
}

function deriveEventAuthorityPda(programId: PublicKey): PublicKey {
  return derivePda([EVENT_AUTHORITY_SEED], programId);
}

function deriveAssociatedTokenAddress(owner: PublicKey, mint: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'),
  )[0];
}

export interface BoostMarketParams extends BuildParams {
  booster: PublicKey;
  market: PublicKey;
  amount: BN;
  /** Rent payer for the booster_quote_ata idempotent-create; defaults to booster. */
  payer?: PublicKey;
}

/**
 * Build a boost_market transaction. Transfers `amount` of quote (USDC, D6) from
 * the booster's quote ATA directly into the market's prize_pool_vault while the
 * market is in Trading state.
 *
 * Wraps IDL instruction `boost_market` — see contracts/markets/idl/markets.json.
 *
 * @param connection - Solana RPC connection.
 * @param params - Boost parameters. See {@link BoostMarketParams}.
 * @returns BuildResult with unsigned transaction ready for signing.
 */
export async function buildBoostMarket(
  connection: Connection,
  params: BoostMarketParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  // ALLOW-UNKNOWN: Anchor `Program<Idl>` and our typed `AnchorProgramAccounts` view of the same handle have structurally incompatible function signatures that TS cannot reconcile without `unknown`.
  const accounts = program as unknown as AnchorProgramAccounts;
  const marketAccount = await accounts.account.market.fetch(params.market);
  const marketConfig = await accounts.account.marketConfig.fetch(marketAccount.config);

  const quoteMint = marketConfig.quoteMint;
  const boosterQuoteAta = deriveAssociatedTokenAddress(params.booster, quoteMint);
  const prizePoolVault = derivePrizePoolVaultPda(params.market, programId);

  const ataPayer = params.payer ?? params.booster;
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.booster, quoteMint, TOKEN_PROGRAM_ID),
  ];

  const instruction = await invokeMethod(program, 'boostMarket', params.amount)
    .accounts({
      booster: params.booster,
      market: params.market,
      marketConfig: marketAccount.config,
      quoteMint,
      boosterQuoteAta,
      prizePoolVault,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  const computeUnits = params.computeUnits ?? estimateComputeUnits('boost_market');
  return createBuildResult(
    [...preInstructions, instruction],
    uniqueSignerKeys(params.booster, ataPayer),
    computeUnits,
  );
}
